import { Container, Graphics, Text } from 'pixi.js';
import { gsap } from 'gsap';

const GOLD = 0xffc94d;
const WHITE = 0xffffff;

/**
 * The battle-royale arena furniture: the ring the flags sit inside, and the
 * winner sunburst that fires when one is left.
 *
 * Both are drawn rather than textured because both animate continuously - the
 * ring's gap sweeps, the sunburst's spokes rotate and pulse - and re-tinting a
 * sprite could not express either.
 */
export class RoyaleRing {
  readonly view = new Container();

  private readonly ring = new Graphics();
  private readonly sweep = new Graphics();
  private readonly burst = new Graphics();
  private readonly winnerLabel: Text;
  private readonly nameLabel: Text;

  private elapsed = 0;
  private burstStrength = 0;
  private radius = 330;

  constructor() {
    this.view.eventMode = 'none';

    this.winnerLabel = caption('WINNER', 62, GOLD);
    this.nameLabel = caption('', 40, WHITE);

    this.burst.alpha = 0;
    this.winnerLabel.alpha = 0;
    this.nameLabel.alpha = 0;

    // Burst sits behind the flags; ring and sweep in front of nothing in
    // particular, but above the burst so the arena edge always reads.
    this.view.addChild(this.burst, this.ring, this.sweep, this.winnerLabel, this.nameLabel);
  }

  /** Place and size the arena. Called when the layout region changes. */
  configure(centreX: number, centreY: number, radius: number): void {
    this.view.position.set(centreX, centreY);
    this.radius = radius;

    this.ring.clear();
    this.ring.circle(0, 0, radius).stroke({ width: 3, color: WHITE, alpha: 0.34 });
    this.ring.circle(0, 0, radius + 16).stroke({ width: 1, color: WHITE, alpha: 0.1 });

    this.winnerLabel.position.set(0, -radius - 74);
    this.nameLabel.position.set(0, radius + 60);

    this.drawBurst();
  }

  /** Fire the winner graphic. */
  celebrate(name: string): void {
    this.nameLabel.text = name.toUpperCase();
    this.burstStrength = 1;

    gsap.killTweensOf([this.burst, this.winnerLabel, this.nameLabel]);
    gsap.to(this.burst, { alpha: 0.85, duration: 0.35, ease: 'power2.out' });
    gsap.fromTo(
      this.winnerLabel,
      { alpha: 0, y: -this.radius - 40 },
      { alpha: 1, y: -this.radius - 74, duration: 0.5, ease: 'back.out(2)' },
    );
    gsap.fromTo(
      this.nameLabel,
      { alpha: 0, y: this.radius + 96 },
      { alpha: 1, y: this.radius + 60, duration: 0.5, delay: 0.1, ease: 'back.out(2)' },
    );
  }

  /** Clear the winner graphic before the next round packs the arena. */
  reset(): void {
    gsap.killTweensOf([this.burst, this.winnerLabel, this.nameLabel]);
    gsap.to([this.burst, this.winnerLabel, this.nameLabel], { alpha: 0, duration: 0.3 });
    this.burstStrength = 0;
  }

  setVisible(visible: boolean): void {
    this.view.visible = visible;
    if (!visible) this.reset();
  }

  update(deltaSeconds: number): void {
    if (!this.view.visible) return;
    this.elapsed += deltaSeconds;

    // Two arcs chasing each other around the rim.
    const head = this.elapsed * 0.9;
    this.sweep.clear();
    this.sweep
      .arc(0, 0, this.radius, head, head + Math.PI * 0.55)
      .stroke({ width: 5, color: WHITE, alpha: 0.95, cap: 'round' });
    this.sweep
      .arc(0, 0, this.radius, head + Math.PI, head + Math.PI * 1.4)
      .stroke({ width: 5, color: WHITE, alpha: 0.55, cap: 'round' });

    if (this.burstStrength > 0) {
      this.burst.rotation += deltaSeconds * 0.24;
      const pulse = 1 + Math.sin(this.elapsed * 4.2) * 0.045;
      this.burst.scale.set(pulse);
    }
  }

  /** Radial spokes, drawn once and then just rotated. */
  private drawBurst(): void {
    const spokes = 18;
    const inner = this.radius * 0.42;
    const outer = this.radius * 1.42;
    const halfWidth = Math.PI / spokes / 2.6;

    this.burst.clear();
    for (let i = 0; i < spokes; i++) {
      const angle = (i / spokes) * Math.PI * 2;
      this.burst
        .poly([
          Math.cos(angle - halfWidth) * inner,
          Math.sin(angle - halfWidth) * inner,
          Math.cos(angle + halfWidth) * inner,
          Math.sin(angle + halfWidth) * inner,
          Math.cos(angle + halfWidth * 0.55) * outer,
          Math.sin(angle + halfWidth * 0.55) * outer,
          Math.cos(angle - halfWidth * 0.55) * outer,
          Math.sin(angle - halfWidth * 0.55) * outer,
        ])
        .fill({ color: GOLD, alpha: 0.3 });
    }

    this.burst.circle(0, 0, this.radius * 0.78).stroke({ width: 2, color: GOLD, alpha: 0.4 });
    this.burst.circle(0, 0, this.radius * 1.02).stroke({ width: 1, color: GOLD, alpha: 0.25 });
  }
}

function caption(text: string, size: number, color: number): Text {
  const node = new Text({
    text,
    style: {
      fontFamily: 'Bahnschrift, "Segoe UI", system-ui, sans-serif',
      fontSize: size,
      fontWeight: '700',
      fill: color,
      letterSpacing: size * 0.14,
      align: 'center',
      dropShadow: { color: 0x04070f, alpha: 0.9, blur: 14, distance: 0, angle: 0 },
    },
  });
  node.anchor.set(0.5);
  return node;
}
